/**
 * Manual Sync — For the "remotest" POS.
 *
 * Users with no API, no webhook, no integration at all.
 * They export a file from their POS, drop it here, and we handle the rest.
 *
 * Flow: Upload → Auto-Map → Preview → Summary → Confirm Sync
 *
 * This is the difference between a sham marketplace and a real one.
 */

import { useState, useCallback, useRef } from 'react'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from '@retailsync/ui'
import { Button } from '@retailsync/ui'
import {
  ArrowUpTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TableCellsIcon,
  EyeIcon,
  CloudArrowUpIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline'
import {
  autoMapFields,
  generateSummaryText,
  type AutoMapping,
  type MappingReport,
  getFieldByEntity,
  getFieldByKey,
} from '../services/smart-field-mapper'
import { parseDataFile, buildSampleValues } from '../services/file-parser'
import { API_ORIGIN } from '../config/api'

// ─── Types ──────────────────────────────────────────────────────────────────

type Entity = 'products' | 'customers' | 'inventory' | 'transactions'
type Step = 'upload' | 'map' | 'preview' | 'summary' | 'syncing'

const ENTITIES: { value: Entity; label: string; icon: any }[] = [
  { value: 'products', label: 'Products', icon: TableCellsIcon },
  { value: 'customers', label: 'Customers', icon: TableCellsIcon },
  { value: 'inventory', label: 'Inventory', icon: TableCellsIcon },
  { value: 'transactions', label: 'Transactions', icon: TableCellsIcon },
]

interface FileData {
  headers: string[]
  rows: Record<string, any>[]
  fileName: string
  totalRows: number
}

interface SyncResult {
  received: number
  created: number
  updated: number
  skipped: number
  errors: string[]
}

const API_BASE = API_ORIGIN

// ─── Main Component ─────────────────────────────────────────────────────────

export function ManualSync() {
  const [step, setStep] = useState<Step>('upload')
  const [entity, setEntity] = useState<Entity>('products')
  const [fileData, setFileData] = useState<FileData | null>(null)
  const [mappingReport, setMappingReport] = useState<MappingReport | null>(null)
  const [manualOverrides, setManualOverrides] = useState<Record<string, string>>({})
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // ── Step 1: Upload & Parse ───────────────────────────────────────────────

  const handleFileDrop = useCallback(async (file: File) => {
    setError(null)
    setLoading(true)
    try {
      // CSV / XLSX / XLS / JSON — all parsed by the shared connector parser.
      const parsed = await parseDataFile(file)
      const headers = parsed.headers
      const dataRows = parsed.rows

      const fd: FileData = {
        headers,
        rows: dataRows,
        fileName: parsed.fileName,
        totalRows: parsed.totalRows,
      }
      setFileData(fd)

      // Auto-map
      const report = autoMapFields(entity, headers, buildSampleValues(dataRows, headers))
      setMappingReport(report)

      // Prime manual overrides
      const overrides: Record<string, string> = {}
      report.detected.forEach(d => { overrides[d.sourceHeader] = d.targetField })
      report.unmapped.forEach(u => { overrides[u] = '' })
      setManualOverrides(overrides)

      setStep('map')
    } catch (err: any) {
      setError(err.message || 'Failed to parse file')
    } finally {
      setLoading(false)
    }
  }, [entity])

  // ── Step 2: Review/Edit Mappings ────────────────────────────────────────────────

  const handleMappingChange = (sourceHeader: string, targetField: string) => {
    setManualOverrides(prev => ({ ...prev, [sourceHeader]: targetField }))
  }

  const getMappingsForSync = (): AutoMapping[] => {
    const mappings: AutoMapping[] = []
    for (const [source, target] of Object.entries(manualOverrides)) {
      if (target) {
        const detected = mappingReport?.detected.find(d => d.sourceHeader === source)
        mappings.push({
          sourceHeader: source,
          targetField: target,
          confidence: detected?.confidence || 0,
          method: detected?.method || 'exact',
        })
      }
    }
    return mappings
  }

  const fields = getFieldByEntity(entity)

  // ── Step 3: Preview ──────────────────────────────────────────────────────

  const previewRows = fileData?.rows.slice(0, 10) || []
  const previewMappings = getMappingsForSync()

  // ── Step 4: Sync ─────────────────────────────────────────────────────────

  const handleSync = async () => {
    if (!fileData) return
    setStep('syncing')
    setError(null)

    const mappings = getMappingsForSync()
    const mappedRows = fileData.rows.map(row => {
      const mapped: Record<string, any> = {}
      for (const m of mappings) {
        mapped[m.targetField] = row[m.sourceHeader]
      }
      return mapped
    })

    const token = localStorage.getItem('access_token') || ''
    const tenantId = localStorage.getItem('tenant_id') || ''

    const endpointMap: Record<Entity, string> = {
      products: '/products/bulk',
      customers: '/customers/bulk',
      inventory: '/inventory/bulk',
      transactions: '/transactions/bulk',
    }

    const keyMap: Record<Entity, string> = {
      products: 'items',
      customers: 'items',
      inventory: 'items',
      transactions: 'items',
    }

    try {
      const res = await fetch(`${API_BASE}/api/v1${endpointMap[entity]}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-tenant-id': tenantId,
        },
        body: JSON.stringify({ [keyMap[entity]]: mappedRows }),
      })

      const json = await res.json()
      if (json.success) {
        setSyncResult({
          received: json.received || fileData.totalRows,
          created: json.created || 0,
          updated: json.updated || 0,
          skipped: (json.received || fileData.totalRows) - ((json.created || 0) + (json.updated || 0)),
          errors: json.errors || [],
        })
        setStep('summary')
      } else {
        setError(json.error || 'Sync failed')
        setStep('map')
      }
    } catch (err: any) {
      setError(err.message || 'Network error')
      setStep('map')
    }
  }

  const reset = () => {
    setStep('upload')
    setFileData(null)
    setMappingReport(null)
    setManualOverrides({})
    setSyncResult(null)
    setError(null)
  }

  // ─── Confidence Badge ────────────────────────────────────────────────────

  function ConfidenceBadge({ confidence }: { confidence: number; method: string }) {
    const config = confidence >= 90
      ? { color: 'bg-green-100 text-green-800', label: 'High' }
      : confidence >= 70
      ? { color: 'bg-amber-100 text-amber-800', label: 'Medium' }
      : { color: 'bg-red-100 text-red-800', label: 'Low' }
    return (
      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${config.color}`}>
        {config.label} ({confidence}%)
      </span>
    )
  }

  // ─── Step Indicator ──────────────────────────────────────────────────────

  const steps: { key: Step; label: string }[] = [
    { key: 'upload', label: 'Upload' },
    { key: 'map', label: 'Map Fields' },
    { key: 'preview', label: 'Preview' },
    { key: 'summary', label: 'Sync' },
  ]

  const currentStepIdx = steps.findIndex(s => s.key === step)

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Manual Sync</h1>
        <p className="text-gray-500 mt-1">
          Import data from any POS or spreadsheet. We auto-detect your columns, you review, then sync.
        </p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-2">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center gap-2">
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${
              currentStepIdx >= i ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-400'
            }`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                currentStepIdx > i ? 'bg-blue-600 text-white' :
                currentStepIdx === i ? 'bg-blue-600 text-white' : 'bg-gray-300 text-gray-500'
              }`}>
                {currentStepIdx > i ? '✓' : i + 1}
              </span>
              {s.label}
            </div>
            {i < steps.length - 1 && <div className="w-6 h-px bg-gray-300" />}
          </div>
        ))}
      </div>

      {/* ─── UPLOAD STEP ─────────────────────────────────────────────────── */}
      {step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Upload Your Data File</CardTitle>
            <CardDescription>
              Export from your POS as CSV or Excel, then drop it here. Works with ANY format.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Entity selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">What are you importing?</label>
              <div className="flex gap-2">
                {ENTITIES.map(e => (
                  <button
                    key={e.value}
                    onClick={() => setEntity(e.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                      entity === e.value
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600'
                    }`}
                  >
                    <e.icon className="w-4 h-4" />
                    {e.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Drop zone */}
            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center hover:border-blue-400 hover:bg-blue-50/30 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-blue-50') }}
              onDragLeave={e => { e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50') }}
              onDrop={e => {
                e.preventDefault()
                e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50')
                const file = e.dataTransfer.files[0]
                if (file) handleFileDrop(file)
              }}
            >
              <ArrowUpTrayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-700">
                Drop your CSV or Excel file here
              </p>
              <p className="text-sm text-gray-500 mt-1">
                or click to browse. Max 10MB, up to 5000 rows per file.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.xlsm,.json"
                className="hidden"
                onChange={e => {
                  const file = e.target.files?.[0]
                  if (file) handleFileDrop(file)
                }}
              />
            </div>

            {loading && (
              <div className="flex items-center justify-center gap-2 text-gray-500">
                <ArrowPathIcon className="w-4 h-4 animate-spin" />
                Parsing file...
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm flex items-start gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Supported formats notice */}
            <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              <InformationCircleIcon className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Any POS export format supported</p>
                <p className="text-blue-600 mt-0.5">
                  Exported from Excel? Saved as CSV from your POS? We auto-detect column names and map them automatically.
                  Just make sure the first row contains column headers.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── MAP STEP ────────────────────────────────────────────────────── */}
      {step === 'map' && fileData && mappingReport && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Review Field Mappings</CardTitle>
                <CardDescription>
                  {mappingReport.suggestions}
                </CardDescription>
              </div>
              <div className="text-sm">
                <span className="font-medium text-green-600">{mappingReport.detected.length}</span> of{' '}
                <span className="font-medium">{fileData.headers.length}</span> columns mapped
                <span className="text-gray-400"> ({mappingReport.coverage}%)</span>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {/* Mapping table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Your File Column</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">→ Maps To</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Confidence</th>
                    <th className="text-left py-2 px-3 font-medium text-gray-500">Sample Values</th>
                  </tr>
                </thead>
                <tbody>
                  {fileData.headers.map(header => {
                    const mapping = mappingReport.detected.find(d => d.sourceHeader === header)
                    const override = manualOverrides[header]
                    const samples = fileData.rows.slice(0, 3).map(r => String(r[header] || '')).join(', ')

                    return (
                      <tr key={header} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-3 font-medium text-gray-900">{header}</td>
                        <td className="py-2 px-3">
                          <select
                            value={override || ''}
                            onChange={e => handleMappingChange(header, e.target.value)}
                            className={`w-full px-2 py-1 border rounded text-sm ${
                              override ? 'border-blue-300 bg-blue-50' : 'border-red-300 bg-red-50'
                            }`}
                          >
                            <option value="">— Skip this column —</option>
                            <optgroup label="Suggested">
                              {fields.map(f => (
                                <option key={f.key} value={f.key}>
                                  {f.label} {f.required ? '(required)' : ''}
                                </option>
                              ))}
                            </optgroup>
                          </select>
                        </td>
                        <td className="py-2 px-3">
                          {mapping ? (
                            <ConfidenceBadge confidence={mapping.confidence} method={mapping.method} />
                          ) : (
                            <span className="text-xs text-red-600">Unmapped</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-xs text-gray-500 max-w-[200px] truncate" title={samples}>
                          {samples}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* Missing required fields */}
            {mappingReport.missing.length > 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 flex items-center gap-1">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  Missing required fields
                </p>
                <ul className="mt-1 text-sm text-amber-700 list-disc list-inside">
                  {mappingReport.missing.map(f => (
                    <li key={f.key}>{f.label} — create a column named "{f.aliases[0]}" in your file</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="ghost" onClick={reset}>Start Over</Button>
              <Button
                onClick={() => setStep('preview')}
                disabled={mappingReport.missing.length > 0}
                className="flex items-center gap-2"
              >
                <EyeIcon className="w-4 h-4" />
                Preview Mapped Data
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── PREVIEW STEP ────────────────────────────────────────────────── */}
      {step === 'preview' && fileData && (
        <Card>
          <CardHeader>
            <CardTitle>Preview Mapped Data</CardTitle>
            <CardDescription>
              Showing first {Math.min(10, fileData.totalRows)} of {fileData.totalRows} rows with their mapped fields.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm border border-gray-200 rounded-lg">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left py-2 px-3 font-medium text-gray-500 border-b">#</th>
                    {previewMappings.map(m => (
                      <th key={m.targetField} className="text-left py-2 px-3 font-medium text-gray-700 border-b">
                        {getFieldByKey(entity, m.targetField)?.label || m.targetField}
                        <div className="text-xs text-gray-400 font-normal">← {m.sourceHeader}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map((row, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-1.5 px-3 text-gray-400">{i + 1}</td>
                      {previewMappings.map(m => (
                        <td key={m.targetField} className="py-1.5 px-3 text-gray-900 max-w-[200px] truncate">
                          {String(row[m.sourceHeader] || '')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="ghost" onClick={() => setStep('map')}>← Back to Mapping</Button>
              <Button
                onClick={handleSync}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
              >
                <CloudArrowUpIcon className="w-4 h-4" />
                Sync {fileData.totalRows} {entity}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── SYNCING ─────────────────────────────────────────────────────── */}
      {step === 'syncing' && (
        <Card>
          <CardContent className="py-12 text-center">
            <ArrowPathIcon className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Syncing your data...</p>
            <p className="text-sm text-gray-500 mt-1">
              Processing {fileData?.totalRows} {entity}. This may take a moment.
            </p>
          </CardContent>
        </Card>
      )}

      {/* ─── SUMMARY STEP ────────────────────────────────────────────────── */}
      {step === 'summary' && syncResult && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6 text-green-500" />
              Sync Complete
            </CardTitle>
            <CardDescription>
              {generateSummaryText(entity, syncResult.received, syncResult.created, syncResult.updated, syncResult.skipped, syncResult.errors)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Results grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="bg-gray-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-gray-900">{syncResult.received}</div>
                <div className="text-xs text-gray-500">Received</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-700">{syncResult.created}</div>
                <div className="text-xs text-green-600">Created</div>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{syncResult.updated}</div>
                <div className="text-xs text-blue-600">Updated</div>
              </div>
              <div className="bg-amber-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-amber-700">{syncResult.skipped}</div>
                <div className="text-xs text-amber-600">Skipped</div>
              </div>
            </div>

            {/* Errors */}
            {syncResult.errors.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-medium text-red-700 mb-2">Errors ({syncResult.errors.length})</h4>
                <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                  {syncResult.errors.map((err, i) => (
                    <div key={i} className="text-xs text-red-700 py-0.5">{err}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Marketing: Upgrade to RetailsyncGo POS */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CloudArrowUpIcon className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold">Tired of manual imports?</h3>
                  <p className="text-blue-100 mt-1 text-sm">
                    Upgrade to <strong>RetailsyncGo POS</strong> — our Electron desktop app that auto-syncs inventory,
                    sales, and customers to your tenant dashboard at startup. No more CSV exports. No more manual uploads.
                    Zero data loss. Works offline.
                  </p>
                  <div className="flex gap-3 mt-4">
                    <a
                      href="https://retailsync.africa"
                      target="_blank"
                      rel="noopener"
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-white text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50 transition-colors"
                    >
                      Get RetailsyncGo POS →
                    </a>
                    <button
                      onClick={reset}
                      className="px-4 py-2 bg-white/10 text-white rounded-lg text-sm font-medium hover:bg-white/20 transition-colors"
                    >
                      Import Another File
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
